import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import Icon from "@mui/material/Icon";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import MDTypography from "components/MDTypography";
import usePortalTheme from "components/portal/usePortalTheme";
import { isPortalNavActive } from "components/portal/portalNav";

function NavSection({ label, children }) {
  const p = usePortalTheme();
  return (
    <Box sx={{ py: 0.5 }}>
      {label ? (
        <MDTypography
          variant="caption"
          sx={{
            px: 2.25,
            pt: 1.25,
            pb: 0.5,
            display: "block",
            color: p.subtle,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontSize: "0.625rem",
          }}
        >
          {label}
        </MDTypography>
      ) : null}
      <List dense disablePadding sx={{ px: 1 }}>
        {children}
      </List>
    </Box>
  );
}

NavSection.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node.isRequired,
};

NavSection.defaultProps = {
  label: null,
};

function NavItem({ item, pathname, onNavigate, cartCount }) {
  const p = usePortalTheme();
  const active = isPortalNavActive(pathname, item.route);
  const danger = item.tone === "danger";

  return (
    <ListItemButton
      selected={active}
      onClick={() => onNavigate(item.route)}
      sx={{
        mb: 0.25,
        borderRadius: `${p.radiusSm}px`,
        color: active ? p.ink : danger ? p.danger : p.muted,
        pl: 1.25,
        pr: 1.5,
        py: 0.85,
        borderLeft: `2px solid ${active ? p.accent : "transparent"}`,
        "&.Mui-selected": {
          bgcolor: p.accentSoft,
          "&:hover": { bgcolor: p.accentSoft },
        },
        "&:hover": {
          bgcolor: danger ? p.dangerSoft : "rgba(17,19,24,0.03)",
          color: danger ? p.danger : p.ink,
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 32,
          color: active ? p.accent : danger ? p.danger : p.subtle,
        }}
      >
        {item.showBadge ? (
          <Badge
            badgeContent={cartCount || 0}
            invisible={!cartCount}
            sx={{
              "& .MuiBadge-badge": {
                bgcolor: p.accent,
                color: "#fff",
                fontSize: "0.5625rem",
                minWidth: 14,
                height: 14,
              },
            }}
          >
            <Icon sx={{ fontSize: 18 }}>{item.icon}</Icon>
          </Badge>
        ) : (
          <Icon sx={{ fontSize: 18 }}>{item.icon}</Icon>
        )}
      </ListItemIcon>
      <ListItemText
        primary={item.label}
        primaryTypographyProps={{
          fontWeight: active ? 700 : 600,
          fontSize: "0.8125rem",
          letterSpacing: "0.01em",
        }}
      />
    </ListItemButton>
  );
}

NavItem.propTypes = {
  item: PropTypes.shape({
    label: PropTypes.string.isRequired,
    route: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    tone: PropTypes.string,
    showBadge: PropTypes.bool,
  }).isRequired,
  pathname: PropTypes.string.isRequired,
  onNavigate: PropTypes.func.isRequired,
  cartCount: PropTypes.number,
};

NavItem.defaultProps = {
  cartCount: 0,
};

export default function PortalSidebar({
  logoUrl,
  brandName,
  roleLabel,
  sections,
  pathname,
  onNavigate,
  onSignOut,
  signOutLabel,
  accountLabel,
  storeLabel,
  cartCount,
}) {
  const p = usePortalTheme();

  return (
    <Box
      component="nav"
      aria-label="Portal navigation"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: p.surface,
        borderRight: `1px solid ${p.lineStrong}`,
      }}
    >
      <Box sx={{ px: 2, pt: 2.25, pb: 1.75 }}>
        <Box
          component="button"
          type="button"
          onClick={() => onNavigate("/account")}
          aria-label={brandName}
          sx={{
            margin: 0,
            padding: 0,
            border: 0,
            background: "none",
            cursor: "pointer",
            display: "block",
            mb: 1.25,
          }}
        >
          <Box
            component="img"
            src={logoUrl}
            alt={brandName}
            sx={{ height: 32, width: "auto", display: "block" }}
          />
        </Box>
        <MDTypography
          variant="caption"
          sx={{
            color: p.subtle,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontSize: "0.625rem",
          }}
        >
          {roleLabel}
        </MDTypography>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        <NavSection label={accountLabel}>
          {sections.account.map((item) => (
            <NavItem
              key={item.route}
              item={item}
              pathname={pathname}
              onNavigate={onNavigate}
              cartCount={cartCount}
            />
          ))}
        </NavSection>

        <NavSection label={storeLabel}>
          {sections.store.map((item) => (
            <NavItem
              key={item.route}
              item={item}
              pathname={pathname}
              onNavigate={onNavigate}
              cartCount={cartCount}
            />
          ))}
        </NavSection>
      </Box>

      <Box sx={{ px: 1, py: 1.25, borderTop: `1px solid ${p.line}` }}>
        {sections.danger.map((item) => (
          <NavItem
            key={item.route}
            item={item}
            pathname={pathname}
            onNavigate={onNavigate}
            cartCount={cartCount}
          />
        ))}
        <List dense disablePadding sx={{ px: 1, pt: 0.5 }}>
          <ListItemButton
            onClick={onSignOut}
            sx={{
              borderRadius: `${p.radiusSm}px`,
              color: p.muted,
              pl: 1.25,
              py: 0.85,
              "&:hover": { bgcolor: "rgba(17,19,24,0.03)", color: p.ink },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: p.subtle }}>
              <Icon sx={{ fontSize: 18 }}>logout</Icon>
            </ListItemIcon>
            <ListItemText
              primary={signOutLabel}
              primaryTypographyProps={{ fontWeight: 600, fontSize: "0.8125rem" }}
            />
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );
}

PortalSidebar.propTypes = {
  logoUrl: PropTypes.string.isRequired,
  brandName: PropTypes.string.isRequired,
  roleLabel: PropTypes.string.isRequired,
  sections: PropTypes.shape({
    account: PropTypes.arrayOf(PropTypes.object).isRequired,
    store: PropTypes.arrayOf(PropTypes.object).isRequired,
    danger: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  pathname: PropTypes.string.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onSignOut: PropTypes.func.isRequired,
  signOutLabel: PropTypes.string.isRequired,
  accountLabel: PropTypes.string.isRequired,
  storeLabel: PropTypes.string.isRequired,
  cartCount: PropTypes.number,
};

PortalSidebar.defaultProps = {
  cartCount: 0,
};
